import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { isStaffBrowser } from '../lib/adminAuth';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Tracks page views AND time-on-page.
 *
 * How duration is measured:
 *   - `startTime`: wall-clock ms at mount
 *   - `visibleMs`: accumulator that only advances while the tab is visible
 *   - On route change / unload we send both to /api/analytics/session using
 *     `navigator.sendBeacon` so the request survives even on hard tab-close.
 */
export const usePageTracking = () => {
  const location = useLocation();
  const startRef = useRef(0);
  const visibleAccumRef = useRef(0);
  const lastVisibleAtRef = useRef(0);
  const currentPageRef = useRef('');

  // Send the accumulated session to the backend.
  const flushSession = (page) => {
    if (!page || page.startsWith('/admin') || !startRef.current || isStaffBrowser()) return;
    const now = Date.now();
    // Add any tail visibility if the tab is still visible.
    if (document.visibilityState === 'visible' && lastVisibleAtRef.current) {
      visibleAccumRef.current += now - lastVisibleAtRef.current;
      lastVisibleAtRef.current = now;
    }
    const duration_ms = now - startRef.current;
    const visibility_ms = Math.min(visibleAccumRef.current, duration_ms);
    if (duration_ms < 1000) return; // ignore accidental clicks

    const body = JSON.stringify({ page, duration_ms, visibility_ms });
    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(`${API}/api/analytics/session`, blob);
      } else {
        axios.post(`${API}/api/analytics/session`, { page, duration_ms, visibility_ms }).catch(() => {});
      }
    } catch {
      // silent
    }
  };

  // Page view tracking (existing behavior)
  useEffect(() => {
    // Flush previous page's session before starting a new one
    if (currentPageRef.current && currentPageRef.current !== location.pathname) {
      flushSession(currentPageRef.current);
    }

    // Reset timers for the new page
    startRef.current = Date.now();
    visibleAccumRef.current = 0;
    lastVisibleAtRef.current = document.visibilityState === 'visible' ? Date.now() : 0;
    currentPageRef.current = location.pathname;

    if (!location.pathname.startsWith('/admin') && !isStaffBrowser()) {
      axios
        .post(`${API}/api/analytics/pageview`, {
          page: location.pathname,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Track visibility changes to accumulate only actually-visible time
  useEffect(() => {
    const handleVisibility = () => {
      const now = Date.now();
      if (document.visibilityState === 'visible') {
        lastVisibleAtRef.current = now;
      } else if (lastVisibleAtRef.current) {
        visibleAccumRef.current += now - lastVisibleAtRef.current;
        lastVisibleAtRef.current = 0;
      }
    };
    const handleUnload = () => flushSession(currentPageRef.current);

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', handleUnload);
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', handleUnload);
      window.removeEventListener('beforeunload', handleUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
